/**
 * KPI Grid Component
 *
 * Displays KPI cards in a responsive grid layout.
 * Features:
 * - Responsive grid (4 columns desktop, 2 tablet, 1 mobile)
 * - Loading skeleton states
 * - Maps KPI data to KPICard components
 */

import React from 'react';
import {
  Box,
  Skeleton,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { KPICard } from './KPICard';
import type { DashboardKPIs } from '../../types/financialInsights';

interface KPIGridProps {
  /** Dashboard KPIs data */
  kpis: DashboardKPIs | null;
  /** Loading state */
  loading: boolean;
}

/**
 * KPI Grid component for displaying financial metrics in a responsive grid
 */
export const KPIGrid: React.FC<KPIGridProps> = ({ kpis, loading }) => {
  /**
   * Render loading skeleton cards
   */
  const renderSkeletons = () => {
    const skeletons = [];
    for (let i = 0; i < 8; i += 1) {
      skeletons.push(
        <Box key={`skeleton-${i}`}>
          <Card sx={{ minHeight: 140 }}>
            <CardContent>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton
                variant="text"
                width="80%"
                height={40}
                sx={{ mt: 2, mb: 1 }}
              />
              <Skeleton variant="text" width="40%" height={20} />
            </CardContent>
          </Card>
        </Box>
      );
    }
    return skeletons;
  };

  /**
   * Render KPI cards from data
   */
  const renderKPICards = () => {
    if (!kpis) {
      return null;
    }

    const kpiList = [
      kpis.total_revenue,
      kpis.cash_balance,
      kpis.accounts_receivable,
      kpis.accounts_payable,
      kpis.active_customers,
      kpis.new_customers,
      kpis.average_receipt_size,
      kpis.on_time_payment_rate,
    ];

    return kpiList.map(metric => (
      <Box key={metric.label}>
        <KPICard metric={metric} />
      </Box>
    ));
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(2, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
        gap: 2,
        position: 'relative',
      }}
    >
      {/* Show loading overlay when data is being refreshed */}
      {loading && kpis && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            borderRadius: 1,
          }}
        >
          <CircularProgress size={40} thickness={4} />
        </Box>
      )}
      {loading && !kpis ? renderSkeletons() : renderKPICards()}
    </Box>
  );
};

export default KPIGrid;
